import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { Bookmark, BookmarkActionViewMode } from '../../../types/api';
import {
  getSelectionPositionClass,
  getBatchSectionPositionClass,
} from '../../../utils/selectionUtils';
import { useBookmarkPageContent } from '../../../contexts/BookmarkPageContext';
import type { AnchorPosition } from '../../ui/Popover';
import BookmarkActions from '../../BookmarkActions';
import BookmarkCheckbox from './BookmarkCheckbox';
import BookmarkTitle from './BookmarkTitle';
import BookmarkMeta from './BookmarkMeta';
import BookmarkDescription from './BookmarkDescription';
import BookmarkThumbnail from './BookmarkThumbnail';
import BookmarkNotesTags from './BookmarkNotesTags';
import BookmarkLocationRow from './BookmarkLocationRow';
import { useBookmarkInteractions } from './hooks/useBookmarkInteractions';
import {
  useBookmarkRowShortcuts,
  type BookmarkActionsView,
} from './hooks/useBookmarkRowShortcuts';
import { useDomainExtraction } from './hooks/useDomainExtraction';
import { useReadingTime } from './hooks/useReadingTime';
import { useThumbnailUrl } from './hooks/useThumbnailUrl';
import { useCurrentUser } from '../../../services/userHooks';
import { bookmarkLocationLabel } from '../../../utils/bookmarkLocation';

interface BookmarkRowProps extends Bookmark {
  condensedView?: boolean;
  showThumbnails?: boolean;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  profileMode?: boolean;
  /** Search results show Home, Archive, or the folder name below the tags. */
  showLocation?: boolean;
}

function BookmarkRow({
  condensedView = false,
  showThumbnails = true,
  viewMode = 'home',
  currentFolderId,
  profileMode = false,
  showLocation = false,
  ...bookmark
}: BookmarkRowProps) {
  const {
    id,
    title,
    url,
    description,
    hash,
    reading_time,
    time,
    author,
    notes,
    tags,
    og_image,
    private_source,
  } = bookmark;

  // Custom hooks for logic extraction
  const {
    isSelected,
    isBatchMode,
    isLoggedIn,
    isMobile,
    showActions,
    isKeyboardNavigable,
    showAriaPressed,
    handleClick,
    handleDoubleClick,
    handleKeyDown,
    ariaLabel,
    selectionContext,
  } = useBookmarkInteractions({
    bookmarkId: id,
    bookmarkTitle: title,
    bookmarkUrl: url,
    profileMode,
  });

  // Get bookmarks array for selection position class
  // Reuse selectionContext from useBookmarkInteractions hook
  const bookmarkPage = useBookmarkPageContent();
  const index =
    bookmarkPage?.bookmarks.findIndex((b) => String(b.id) === String(id)) ?? -1;
  const selectionPositionClass =
    bookmarkPage && selectionContext && isBatchMode && isSelected && index >= 0
      ? selectionContext.sectionBatchTarget
        ? getBatchSectionPositionClass(index, bookmarkPage.bookmarks.length)
        : getSelectionPositionClass(
            id,
            index,
            bookmarkPage.bookmarks,
            selectionContext.selectedBookmarkIds,
          )
      : null;

  // Check if this bookmark is currently being read (active state)
  // Coerce to string so we match even if one side is number (e.g. from URL or API)
  const isActive =
    bookmarkPage?.activeBookmark != null &&
    String(bookmarkPage.activeBookmark.id) === String(id);

  const { currentUser } = useCurrentUser();
  const location = showLocation
    ? bookmarkLocationLabel(bookmark, currentUser?.folders)
    : undefined;

  const domain = useDomainExtraction({ url });
  const source = private_source || domain;
  const readTimeText = useReadingTime({ reading_time });
  const thumbnailUrl = useThumbnailUrl({
    bookmarkId: id,
    hash,
    profileMode,
    ogImage: og_image,
  });

  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsView, setActionsView] = useState<BookmarkActionsView>('menu');
  const [actionsAnchorPosition, setActionsAnchorPosition] =
    useState<AnchorPosition | null>(null);

  const handleActionsOpenChange = useCallback((open: boolean) => {
    setActionsOpen(open);
    // Clear anchor position when popover closes, and when opening via button, so next open uses the trigger
    setActionsAnchorPosition(null);
    if (!open) setActionsView('menu');
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!showActions) return;
      if (e.altKey || e.ctrlKey) return;
      const target = e.target as Element;
      if (target.closest('a')) return;
      e.preventDefault();
      setActionsView('menu');
      setActionsAnchorPosition({ x: e.clientX, y: e.clientY });
      setActionsOpen(true);
    },
    [showActions],
  );

  // Open the "..." popover from a keyboard shortcut, anchored to its trigger.
  const openActions = useCallback((view: BookmarkActionsView) => {
    setActionsAnchorPosition(null);
    setActionsView(view);
    setActionsOpen(true);
  }, []);

  // Off while the menu is open so its own accelerators and the tag input own
  // the keyboard.
  const keyboardTargetingEnabled =
    showActions &&
    !profileMode &&
    !isBatchMode &&
    !isMobile &&
    !actionsOpen &&
    bookmarkPage?.readerLayout === '2col';

  // Single-key article actions (a/l/m/t/e/d) while this row has keyboard focus
  // in the list-only layout.
  const { handleShortcutKeyDown } = useBookmarkRowShortcuts({
    bookmark,
    viewMode,
    enabled: keyboardTargetingEnabled,
    openActions,
  });

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    // Navigation/selection first; the shortcut handler bails on keys it claimed.
    handleKeyDown(event);
    handleShortcutKeyDown(event);
  };

  // Hovering a row makes it the shortcut target, so the keys work without
  // arrowing to it first. Implemented as real focus rather than a separate
  // hover target, so every keyboard path — shortcuts, Enter to open, arrows
  // continuing from here — reads the same row.
  //
  // Chrome matches :focus-visible on a programmatic focus() call, which would
  // draw the keyboard focus ring on plain hover; `data-hover-focus` lets the
  // stylesheet suppress it, leaving the ordinary hover treatment.
  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!keyboardTargetingEnabled) return;
    // Never take the keyboard away from whatever already holds it: a search
    // field, an open menu, or a row the user arrowed to.
    if (document.activeElement && document.activeElement !== document.body) return;
    event.currentTarget.dataset.hoverFocus = 'true';
    event.currentTarget.focus({ preventScroll: true });
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const row = event.currentTarget;
    delete row.dataset.hoverFocus;
    if (document.activeElement !== row) return;
    // The arrow-key cursor outlives the pointer; hover targeting does not.
    if (row.classList.contains('keyboard-focused')) return;
    row.blur();
  };

  // Focus moving away by any route (arrow keys, Tab, opening the menu) ends
  // the hover-focus state, so the ring is never suppressed on a real one.
  const handleRowBlur = (event: React.FocusEvent<HTMLElement>) => {
    delete event.currentTarget.dataset.hoverFocus;
  };

  return (
    <label
      className={clsx(
        'bookmark-item',
        condensedView && 'condensed',
        profileMode && 'profile-mode',
        // Selection states
        isBatchMode && 'batch-mode',
        isBatchMode && isSelected && 'selected',
        // Selection position class for connected styling
        selectionPositionClass,
        // Active state when bookmark is being read
        !isBatchMode && isActive && 'active',
        // Mobile edit mode opacity
        isMobile && isBatchMode && !isSelected && 'opacity-60',
        // Mobile class for hover behavior
        isMobile && 'mobile',
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleRowKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={handleRowBlur}
      tabIndex={isKeyboardNavigable ? 0 : -1}
      aria-pressed={showAriaPressed ? isSelected : undefined}
      aria-label={ariaLabel}
    >
      {/* Checkbox for selection - always render but hide in normal mode */}
      {isLoggedIn && selectionContext && (
        <BookmarkCheckbox
          bookmarkId={id}
          bookmarkTitle={title}
          bookmarkUrl={url}
          isSelected={isSelected}
          isBatchMode={isBatchMode}
          selectionContext={selectionContext}
          className={clsx(isBatchMode && 'batch-mode', condensedView && 'condensed')}
        />
      )}
      <div
        className={clsx(
          'bookmark-content',
          isBatchMode && 'batch-mode',
          condensedView && 'condensed',
          profileMode && 'profile-mode',
        )}
      >
        {/* Main content row with meta, description, notes/tags and thumbnail */}
        <div className="bookmark-content-body">
          <div className="bookmark-row-header">
            <div className="bookmark-row-header-main">
              <BookmarkTitle
                bookmarkId={id}
                title={title}
                url={url}
                isLoggedIn={isLoggedIn}
                condensedView={condensedView}
                profileMode={profileMode}
                isBatchMode={isBatchMode}
                hasActiveReaderArticle={
                  !!bookmarkPage?.activeBookmark &&
                  String(bookmarkPage.activeBookmark.id) !== String(id)
                }
              />
              <BookmarkMeta
                domain={source}
                url={url}
                readTimeText={readTimeText}
                time={time}
                author={author}
                hideAuthor={bookmarkPage?.readerLayout === '3col'}
              />
            </div>
            {showActions && (
              <div className="bookmark-actions">
                <BookmarkActions
                  key={actionsAnchorPosition ? 'virtual' : 'trigger'}
                  bookmark={bookmark}
                  viewMode={viewMode}
                  currentFolderId={currentFolderId}
                  open={actionsOpen}
                  onOpenChange={handleActionsOpenChange}
                  anchorPosition={actionsAnchorPosition}
                  initialView={actionsView}
                />
              </div>
            )}
          </div>
          <BookmarkDescription description={description} condensedView={condensedView} />
          <BookmarkNotesTags notes={notes} tags={tags} bookmarkId={id} />
          {location && <BookmarkLocationRow location={location} />}
        </div>
        {/* Thumbnail - all the way to the right */}
        <BookmarkThumbnail
          thumbnailUrl={thumbnailUrl}
          showThumbnails={showThumbnails}
          condensedView={condensedView}
        />
      </div>
    </label>
  );
}
export default BookmarkRow;
